import {CommandType} from '@ispm/shared/bpmn-io/bpmn-js';
import {CreateSubprocessChildrenHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/create-subprocess-children-handler';
import {NodeDataCreationHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/node-data-creation-handler';
import {AppendConditionExpressionHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/append-condition-expression-handler';
import Modeling from 'diagram-js/lib/features/modeling/Modeling';
import CommandHandler from 'diagram-js/lib/command/CommandHandler';
import {Connection, Shape} from 'diagram-js/lib/model';
import {NodeCreationResult} from '@ispm-client/modules/bpmn-modeler/models/node-creation-result';
import {NodeClearDataHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/node-clear-data-handler';
import {NodeResetDataHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/node-reset-data-handler';
import {select, Store} from '@ngrx/store';
import {State} from '@ispm-client/modules/bpmn-modeler/store/state';
import {NodeResetChildrenDataHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/node-reset-children-data-handler';
import {RemoveNodeHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/remove-node-handler';
import {AppendNodeHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/append-node-handler';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {ProcessBpmnForApprovalHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/tracking-changes/process-bpmn-for-approval-handler';
import {selectSaveParams} from '@ispm-client/modules/bpmn-modeler/store/selectors';
import {selectModelerState} from '@ispm-client/modules/bpmn-modeler/store/selectors/modeler-state.selector';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {Observable} from 'rxjs';
import {PathwaysAuthoringState} from '@ispm/shared/constants/pathways-authoring-state';
import {MarkNodeDeletedHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/tracking-changes/mark-element-deleted/handlers/mark-node-deleted-handler';
import {SaveParams} from '@ispm-client/modules/bpmn-modeler/models/save-params';
import {CalculateNodeChildsDataHandler} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/calculate-node-childs-data-handler';
import {PasteNodesHandler} from "@ispm-client/modules/bpmn-modeler/bpmn/commands/paste-nodes-handler";
import {RemoveConnectionHandler} from "@ispm-client/modules/bpmn-modeler/bpmn/commands/remove-connection-handler";
import {MarkConnectionDeletedHandler} from "@ispm-client/modules/bpmn-modeler/bpmn/tracking-changes/mark-element-deleted/handlers/mark-connection-deleted-handler";
import {AppendConnectionHandler} from "@ispm-client/modules/bpmn-modeler/bpmn/commands/append-connection-handler";
import {ModelingUtil} from "@ispm/shared/bpmn-io/modeling-util";
import {TreeNavigator} from "@ispm/shared/bpmn-io/services/tree-navigator";
import {CopyNodeHierarchyHandler} from "@ispm-client/modules/bpmn-modeler/bpmn/commands/copy-node-hierarchy-handler";
import {CopyNodeHandler} from "@ispm-client/modules/bpmn-modeler/bpmn/commands/copy-node-handler";
import {BackupAndRestoreBpmnBeforeApproval} from "@ispm-client/modules/bpmn-modeler/bpmn/tracking-changes/backup-and-restore-bpmn-before-approval";

export class PathwaysModeling extends Modeling {
  private pathwaysAuthoringState: PathwaysAuthoringState;
  private finalDeployment: boolean;

  constructor(private eventBus, private elementFactory, private commandStack,
              private modeling, private store$: Store<State>, private treeNavigator: TreeNavigator) {
    super(eventBus, elementFactory, commandStack);
    const pathwaysAuthoringState$: Observable<PathwaysAuthoringState> = this.store$.pipe(select(selectModelerState));
    const saveParams$: Observable<SaveParams> = this.store$.pipe(select(selectSaveParams));
    combineLatest(pathwaysAuthoringState$, saveParams$)
      .subscribe(([pathwaysAuthoringState, saveParams]) => {
        this.pathwaysAuthoringState = pathwaysAuthoringState;
        if (saveParams) {
          this.finalDeployment = saveParams.deployAsFinal;
        }
      });
  }


  getHandlers(): { [key: string]: CommandHandler } {
    const handlers = {};
    handlers[CommandType.SUBPROCESS_CREATE_CHILDREN] = CreateSubprocessChildrenHandler;
    handlers[CommandType.NODE_CREATE_DATA] = NodeDataCreationHandler;
    handlers[CommandType.NODE_UPDATE_DATA] = NodeDataCreationHandler; // used to distinguish between create and update handlers[CommandType.NODE_UPDATE_DATA] = NodeDataCreationHandler
    handlers[CommandType.CONNECTION_APPEND_EXPRESSION] = AppendConditionExpressionHandler;
    handlers[CommandType.CALCULATE_CHILDS_DATA] = CalculateNodeChildsDataHandler;
    handlers[CommandType.NODE_CLEAR_DATA] = NodeClearDataHandler;
    handlers[CommandType.NODE_RESET_DATA] = NodeResetDataHandler;
    handlers[CommandType.NODE_RESET_CHILDREN_DATA] = NodeResetChildrenDataHandler;
    handlers[CommandType.REMOVE_NODE] = RemoveNodeHandler;
    handlers[CommandType.APPEND_NODE] = AppendNodeHandler;
    handlers[CommandType.PROCESS_BPMN_FOR_APPROVAL] = ProcessBpmnForApprovalHandler;
    handlers[CommandType.MARK_NODE_DELETED] = MarkNodeDeletedHandler;
    handlers[CommandType.NODE_COPY] = CopyNodeHandler;
    handlers[CommandType.NODE_PASTE] = PasteNodesHandler;
    handlers[CommandType.MARK_CONNECTION_DELETED] = MarkConnectionDeletedHandler;
    handlers[CommandType.REMOVE_CONNECTION] = RemoveConnectionHandler;
    handlers[CommandType.CONNECTION_APPEND] = AppendConnectionHandler;
    handlers[CommandType.NODE_HIERARCHY_COPY] = CopyNodeHierarchyHandler;
    handlers[CommandType.BACKUP_AND_RESTORE_BPMN_BEFORE_APPROVAL] = BackupAndRestoreBpmnBeforeApproval;
    return handlers;
  }

  public createSubProcessChildren(element: Shape) {
    const context = {element};
    this.commandStack.execute(CommandType.SUBPROCESS_CREATE_CHILDREN, context);
    return context.element;
  }

  public createNodeData(element: Shape, nodeCreationResult: NodeCreationResult, nodeType: NodeType) {
    this.commandStack.execute(CommandType.NODE_CREATE_DATA, {element, nodeCreationResult, nodeType});
  }


  public updateNodeData(element: Shape, nodeCreationResult: NodeCreationResult, nodeType: NodeType, ignoreTrackingChange?: boolean) {
    this.commandStack.execute(CommandType.NODE_UPDATE_DATA, {
      element,
      nodeCreationResult,
      nodeType,
      ignoreTrackingChange
    });
  }

  public appendConnectionExpression(connection: Connection, expression: string) {
    this.commandStack.execute(CommandType.CONNECTION_APPEND_EXPRESSION, {connection, expression});
  }

  public calculateChildsData(element: Shape, nodeCreationResults: Array<NodeCreationResult>) {
    this.commandStack.execute(CommandType.CALCULATE_CHILDS_DATA, {
      parentElement: element,
      nodeCreationResults: nodeCreationResults
    });
  }


  public resetNodeData(element: Shape, nodeCreationResult: NodeCreationResult, nodeType: NodeType, ignoreTrackingChange? : boolean) {
    if (!ModelingUtil.hasDeletionTrackingChange(element)) {
      this.commandStack.execute(CommandType.NODE_RESET_DATA, {element: element, nodeCreationResult, nodeType,ignoreTrackingChange});
    }
  }

  public clearNodeData(element: Shape) {
    this.commandStack.execute(CommandType.NODE_CLEAR_DATA, {element});
  }

  public clearConnectionConditionExpression(connection: Connection) {
    let conditionExpression = connection.businessObject.conditionExpression;
    if (conditionExpression) {
      conditionExpression.body = '';
      conditionExpression.language = 'javascript';

    } else {
      conditionExpression = null;
    }
    this.modeling.updateProperties(connection, {conditionExpression: conditionExpression});
  }

  public resetNodeChildrenData(element: Shape, childrenData: Array<NodeCreationResult>) {
    this.commandStack.execute(CommandType.NODE_RESET_CHILDREN_DATA, {element, childrenData});
  }

  public removeNode(element: Shape, onSubmitForApproval?: boolean,onRemoveFloatedElements?: boolean) {
    this.commandStack.execute(CommandType.REMOVE_NODE, {element, onSubmitForApproval, onRemoveFloatedElements});
  }

  public deleteConnection(element: Connection, onSubmitForApproval?: boolean) {
    this.commandStack.execute(CommandType.REMOVE_CONNECTION, {element, onSubmitForApproval});
  }

  public markNodeDeleted(element: Shape) {
    this.commandStack.execute(CommandType.MARK_NODE_DELETED, {element});
  }

  public markConnectionDeleted(element: Connection) {
    this.commandStack.execute(CommandType.MARK_CONNECTION_DELETED, {element});
  }

  public appendNewNode(source: Shape, type: string, nodeType: NodeType, attrs?, params?) {
    const context = {source, type, node: undefined, nodeType, attrs, params};
    this.commandStack.execute(CommandType.APPEND_NODE, context);
    return context.node;
  }

  public appendNewConnection(source: Shape, target: Shape, originalSource: Shape,gateway?: Shape) {
    const connection = gateway ? ModelingUtil.getConnection(source, gateway): ModelingUtil.getConnection(source, target);
    if (connection) {
      const context = gateway ? {connection: connection, originalSource: originalSource, target: target} : {connection: connection, originalSource: originalSource};
      this.commandStack.execute(CommandType.CONNECTION_APPEND, context);
    }

  }

  public moveElements(shapes, delta, target, hints) {

    hints = hints || {};
    const attach = hints.attach;
    let newParent = target,
      newHost;

    if (attach === true) {
      newHost = target;
      newParent = target.parent;
    } else if (attach === false) {
      newHost = null;
    }
    const visibleShapes = shapes.filter(element => !element.hidden);
    const filteredVisibleShapes = visibleShapes.filter(element => (element.businessObject.neighborhoodID && element.businessObject.neighborhoodID === this.treeNavigator.currentLayer));
    let context = {
      shapes: filteredVisibleShapes,
      delta: delta,
      newParent: newParent,
      newHost: newHost,
      hints: hints
    };

    this.commandStack.execute(CommandType.ELEMENTS_MOVE, context);
  }


  public setElementVisibility(element: Shape, hidden: boolean) {
    this.commandStack.execute(CommandType.SET_ELEMENT_VISIBILITY, {element, hidden});
  }

  public processBpmnForApproval() {
    this.commandStack.execute(CommandType.PROCESS_BPMN_FOR_APPROVAL, {
      pathwaysAuthoringState: this.pathwaysAuthoringState,
      finalDeployment: this.finalDeployment,
      readyForPreview: false
    });
  }

  public processBpmnForPreview() {
    this.commandStack.execute(CommandType.PROCESS_BPMN_FOR_APPROVAL, {
      pathwaysAuthoringState: this.pathwaysAuthoringState,
      finalDeployment: false,
      readyForPreview: true
    });
  }

  public restoreBpmnBeforeApproval() {
    this.commandStack.execute(CommandType.BACKUP_AND_RESTORE_BPMN_BEFORE_APPROVAL, {restoreTrackingChanges: true});
  }

  removeElements(elements) {
    if(elements){
      super.removeElements(elements);
    }
  }

  removeShape(shape, hints) {
    super.removeShape(shape, hints);
  }

  removeConnection(connection, hints) {
    super.removeConnection(connection, hints);
  }

  pasteNode(element) {
    this.commandStack.execute(CommandType.NODE_PASTE, {element});
  }

  copyNode(element) {
    this.commandStack.execute(CommandType.NODE_COPY, {element});
  }
  copyNodeHierarchy(element) {
    this.commandStack.execute(CommandType.NODE_HIERARCHY_COPY, {element});
  }

}

PathwaysModeling.$inject = [...Modeling.$inject, 'modeling', 'store$', 'treeNavigator'];
